import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

/** Root of a modal dialog (controls open state). */
export const Dialog = DialogPrimitive.Root

/** Dimmed backdrop rendered behind the dialog panel. */
const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(function DialogOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn('fixed inset-0 z-50 bg-scrim motion-safe:animate-[ds-fade-in_150ms_var(--ds-ease-out)]', className)}
      {...props}
    />
  )
})

/** The centered dialog panel, portaled to the document body. */
export const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(function DialogContent({ className, children, ...props }, ref) {
  return (
    <DialogPrimitive.Portal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-1/2 top-1/2 z-50 grid w-full max-w-[480px] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-lg border border-line bg-surface px-6 pb-5 pt-[22px] text-ink shadow-lg',
          // Grid items default to min-width:auto, so one unbreakable string — a long
          // email, a URL, a file name — widens the track and spills the dialog's own
          // content past its edge. Letting children shrink keeps that inside.
          '[&>*]:min-w-0',
          'motion-safe:animate-[ds-dialog-in_150ms_var(--ds-ease-out)]',
          className,
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute right-3.5 top-3.5 grid h-[30px] w-[30px] place-items-center rounded-md text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink focus-visible:focus-ds">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
})

/** Groups the dialog's title and description at the top of the panel. */
export function DialogHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('grid gap-1.5 pr-8 text-left', className)}
      {...props}
    />
  )
}

/** Groups the dialog's action buttons at the bottom of the panel. */
export function DialogFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:flex-wrap sm:justify-end',
        className,
      )}
      {...props}
    />
  )
}

/** The accessible title of the dialog. */
export const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(function DialogTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn(
        'text-title-sm font-semibold text-ink [text-wrap:balance]',
        className,
      )}
      {...props}
    />
  )
})

/** The accessible description of the dialog. */
export const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(function DialogDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn('text-sm text-ink-muted', className)}
      {...props}
    />
  )
})
